import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, Trash2, Database, Calendar, Hash, Sparkles, Check, Gauge, HeartPulse, Layers, ArrowRight, Clock, BedDouble, ShieldCheck, ShieldAlert, ShieldQuestion, Lightbulb, Lock, History, XCircle, TrendingUp, TrendingDown } from "lucide-react";

const LEARN_RESULT = {
  worked: { color: "#22c55e", Icon: CheckCircle2, it: "Ha funzionato", en: "Worked" },
  could_push: { color: "#0ea5e9", Icon: TrendingUp, it: "Potevi osare di più", en: "Could push more" },
  sold_below: { color: "#f59e0b", Icon: TrendingDown, it: "Margine perso", en: "Lost margin" },
  unsold: { color: "#ef4444", Icon: XCircle, it: "Non venduta", en: "Unsold" },
  superseded: { color: "#ef4444", Icon: XCircle, it: "Non ha funzionato (sostituito)", en: "Didn't work (superseded)" },
  pre_booked: { color: "#8b5cf6", Icon: Clock, it: "Già prenotata prima", en: "Booked before" },
  pending: { color: "#94a3b8", Icon: Clock, it: "In attesa", en: "Pending" },
};

export default function DataImport() {
  const { lang, properties, activeId, selectProperty } = useApp();
  const L = (it, en) => (lang === "it" ? it : en);
  const [pid, setPid] = useState(activeId);
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dataSource, setDataSource] = useState("");
  const [pmsName, setPmsName] = useState("");
  const [retain, setRetain] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [health, setHealth] = useState(null);
  const [avail, setAvail] = useState(null);
  const [trust, setTrust] = useState(null);
  const [learning, setLearning] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (!pid && activeId) setPid(activeId); }, [activeId, pid]);

  const loadHistory = useCallback(async () => {
    if (!pid) return;
    try { const { data } = await api.get(`/import/batches?property_id=${pid}`); setBatches(data.batches || []); setKpi(data.kpi); }
    catch { /* noop */ }
    try { const { data } = await api.get(`/import/completeness?property_id=${pid}&lang=${lang}`); setHealth(data); }
    catch { setHealth(null); }
    try { const { data } = await api.get(`/import/availability?property_id=${pid}`); setAvail(data); }
    catch { setAvail(null); }
    try { const { data } = await api.get(`/import/data-trust?property_id=${pid}`); setTrust(data); }
    catch { setTrust(null); }
  }, [pid, lang]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const reprocess = async () => {
    if (!pid) { toast.error(L("Seleziona prima una struttura", "Select a property first")); return; }
    setReprocessing(true);
    try {
      const { data } = await api.post(`/import/reprocess?property_id=${pid}`);
      if (data.files_reprocessed === 0 && data.booking_dates === 0 && data.rate_plans === 0) {
        toast.warning(L(
          "Nessun file conservato da ri-analizzare. Ricarica il tuo file (assicurati che le colonne 'Data prenotazione' e 'Trattamento' siano presenti).",
          "No retained file to re-analyze. Re-upload your file (make sure the 'booking date' and 'meal plan' columns are present)."));
      } else {
        toast.success(L(
          `Ri-analisi completata: ${data.files_reprocessed} file · ${data.rate_plans} tariffe con trattamento · ${data.booking_dates} con data prenotazione`,
          `Reprocess done: ${data.files_reprocessed} files · ${data.rate_plans} rates with meal plan · ${data.booking_dates} with booking date`));
      }
      loadHistory();
    } catch (e) {
      toast.error(L("Errore durante la ri-analisi", "Reprocess failed"));
    } finally { setReprocessing(false); }
  };

  const onFile = async (file) => {
    if (!file || !pid) { if (!pid) toast.error(L("Seleziona prima una struttura", "Select a property first")); return; }
    setBusy(true); setResult(null); setAnalysis(null);
    try {
      const fd = new FormData();
      fd.append("property_id", pid); fd.append("file", file);
      const { data } = await api.post("/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      setDataSource(data.data_source || "generic");
      setPmsName(data.pms_name || "");
      // ONLY auto (>=95%) mappings are pre-selected. Suggested (70-94%) need explicit Accept.
      setMapping(Object.fromEntries(Object.entries(data.suggestions || {})
        .filter(([, v]) => v.tier === "auto")
        .map(([f, v]) => [f, v.column])));
      if (data.already_imported) toast.warning(L("Questo file risulta già importato in precedenza.", "This file was already imported before."));
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore analisi file", "File analysis error")); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/confirm", { upload_id: analysis.upload_id, mapping, data_source: dataSource, pms_name: pmsName || null, retain_file: retain });
      setResult(data); setKpi(data.kpi); setAnalysis(null);
      toast.success(L("Importazione completata", "Import completed"));
      // Learning loop: if any advice was applied, show the "did it work?" summary popup.
      if (data.applied_learning && data.applied_learning.total_applied > 0) {
        setLearning(data.applied_learning);
      }
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore importazione", "Import error")); }
    finally { setBusy(false); }
  };

  const del = async (batchId) => {
    if (!window.confirm(L("Eliminare questa importazione e i relativi record?", "Delete this import and its records?"))) return;
    try { const { data } = await api.delete(`/import/batches/${batchId}`); setKpi(data.kpi); toast.success(L("Eliminata", "Deleted")); loadHistory(); }
    catch { toast.error(L("Errore", "Error")); }
  };

  const fieldLabels = analysis?.field_labels || {};
  const dsOptions = analysis?.data_sources || [];
  const dsLabel = (key) => { const o = dsOptions.find((x) => x.key === key); return o ? L(o.label.it, o.label.en) : key; };
  // fields to show: expected for the source + anything already mapped/suggested
  const rowFields = analysis ? Array.from(new Set([
    ...(analysis.expected_fields || []),
    ...Object.keys(analysis.suggestions || {}),
    ...Object.keys(mapping),
  ])) : [];
  const acceptSuggestion = (f) => setMapping((m) => ({ ...m, [f]: analysis.suggestions[f].column }));

  return (
    <div className="min-h-screen bg-background">
      <Nav />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2"><Database className="w-7 h-7 text-primary" />{L("Universal Import Engine", "Universal Import Engine")}</h1>
          <p className="text-muted-foreground mt-1">{L("Carica un export CSV/XLSX da qualsiasi PMS o Channel Manager: il sistema riconosce la fonte dati e le colonne, senza mai forzare mappature incerte.", "Upload a CSV/XLSX export from any PMS or Channel Manager: the system recognizes the data source and columns, never forcing uncertain mappings.")}</p>
        </div>

        {/* property selector */}
        <div className="mb-6 max-w-sm">
          <Label>{L("Struttura", "Property")}</Label>
          <Select value={pid || ""} onValueChange={(v) => { setPid(v); selectProperty(v); }}>
            <SelectTrigger data-testid="import-property-select" className="mt-1"><SelectValue placeholder={L("Seleziona…", "Select…")} /></SelectTrigger>
            <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Data Health & Completeness */}
        {health && (
          <DataHealthPanel health={health} L={L} lang={lang} />
        )}

        {/* Data Trust Score */}
        {trust?.analyzed && (
          <TrustScorePanel trust={trust} L={L} lang={lang} />
        )}

        {/* Availability Reconstruction */}
        {avail?.availability && (
          <AvailabilityPanel avail={avail} L={L} />
        )}

        {/* KPI dashboard */}
        {kpi && (
          <div data-testid="import-kpi" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {[
              { icon: Calendar, label: L("Periodo coperto", "Period covered"), value: kpi.period ? `${kpi.period.from} → ${kpi.period.to}` : "—" },
              { icon: Hash, label: L("Giorni analizzati", "Days analyzed"), value: kpi.days ?? "—" },
              { icon: Database, label: L("Ricavi totali", "Total revenue"), value: kpi.revenue_total != null ? `€${kpi.revenue_total}` : "—" },
              { icon: Clock, label: L("Ultimo aggiornamento", "Last update"), value: kpi.computed_at ? new Date(kpi.computed_at).toLocaleString(lang === "it" ? "it-IT" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—" },
            ].map((k, i) => (
              <div key={i} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1"><k.icon className="w-3.5 h-3.5" />{k.label}</div>
                <div className="text-sm font-data font-bold">{k.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Upload zone */}
        {!analysis && (
          <div data-testid="import-dropzone"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
            className="border-2 border-dashed border-border rounded-xl p-10 text-center cursor-pointer hover:border-primary/50 hover:bg-accent/30 transition-colors">
            <input ref={fileRef} data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls,.xlsm" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            {busy ? <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" /> : <UploadCloud className="w-10 h-10 mx-auto text-primary" />}
            <p className="mt-3 font-semibold">{busy ? L("Analisi in corso…", "Analyzing…") : L("Trascina qui il file o clicca per caricare", "Drop your file here or click to upload")}</p>
            <p className="text-sm text-muted-foreground mt-1">CSV · XLSX — {L("separatore e codifica riconosciuti automaticamente", "separator & encoding auto-detected")}</p>
          </div>
        )}

        {/* Mapping review */}
        {analysis && (
          <motion.div data-testid="import-review" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                <span className="font-semibold">{analysis.filename}</span>
                <span className="text-xs text-muted-foreground">· {analysis.sample_count} {L("righe", "rows")} · sep “{analysis.separator}”</span>
              </div>
              {analysis.library_match
                ? <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: "color-mix(in srgb, var(--demand-low) 16%, transparent)", color: "var(--demand-low)" }}><Sparkles className="w-3.5 h-3.5" />{L("Formato riconosciuto automaticamente", "Format auto-recognized")}{analysis.pms_name ? ` · ${analysis.pms_name}` : ""}</span>
                : <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">{L("Nuovo formato — verrà appreso alla conferma", "New format — will be learned on confirm")}</span>}
            </div>

            {/* Data Source + expected coverage */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <Label>{L("Fonte dati (Data Source)", "Data Source")}</Label>
                <Select value={dataSource} onValueChange={setDataSource}>
                  <SelectTrigger data-testid="import-data-source" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{dsOptions.map((o) => <SelectItem key={o.key} value={o.key}>{L(o.label.it, o.label.en)}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">{L("Il sistema ha riconosciuto", "The system recognized")}: <b>{dsLabel(analysis.data_source)}</b></p>
              </div>
              <div>
                <Label>{L("PMS / Channel Manager (opzionale)", "PMS / Channel Manager (optional)")}</Label>
                <Input data-testid="import-pms-name" value={pmsName} onChange={(e) => setPmsName(e.target.value)} placeholder="Beddy, Cloudbeds, Smoobu…" className="mt-1" />
              </div>
            </div>

            {/* Columns present vs missing for this source */}
            <div data-testid="import-coverage" className="rounded-lg border border-border bg-secondary/30 p-4 mb-5">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-2 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" />{L("Colonne attese per", "Expected columns for")} “{dsLabel(dataSource)}”</div>
              <div className="flex flex-wrap gap-1.5">
                {(analysis.expected_fields || []).map((f) => {
                  const has = !!mapping[f] || (analysis.present_fields || []).includes(f);
                  return (
                    <span key={f} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
                      style={has
                        ? { backgroundColor: "color-mix(in srgb, var(--demand-low) 16%, transparent)", color: "var(--demand-low)" }
                        : { backgroundColor: "var(--secondary)", color: "var(--muted-foreground)" }}>
                      {has ? <Check className="w-3 h-3" /> : <span className="opacity-60">—</span>}{fieldLabels[f] || f}
                    </span>
                  );
                })}
                {(analysis.expected_fields || []).length === 0 && <span className="text-xs text-muted-foreground">{L("Nessuno schema specifico", "No specific schema")}</span>}
              </div>
            </div>

            {analysis.suggested_fields?.length > 0 && (
              <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-4 py-3 text-sm dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                {L("Alcune colonne hanno una confidenza tra 70-94%: sono solo suggerimenti. Premi “Accetta” per confermarle — nessuna viene applicata automaticamente.", "Some columns have 70-94% confidence: these are suggestions only. Press “Accept” to confirm them — none is applied automatically.")}
              </div>
            )}

            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-bold px-3 py-2">{L("Campo Revenue AI", "Revenue AI field")}</th>
                  <th className="text-left font-bold px-3 py-2">{L("Colonna del file", "File column")}</th>
                  <th className="text-right font-bold px-3 py-2">{L("Stato", "Status")}</th>
                </tr></thead>
                <tbody>
                  {rowFields.map((f) => {
                    const sug = analysis.suggestions?.[f];
                    const conf = sug?.confidence;
                    const tier = sug?.tier;
                    const mapped = mapping[f] || "";
                    const isSuggested = tier === "suggested" && !mapped;
                    return (
                      <tr key={f} data-testid={`map-row-${f}`} className={`border-t border-border ${isSuggested ? "bg-amber-50/60 dark:bg-amber-500/5" : ""}`}>
                        <td className="px-3 py-2 font-medium">{fieldLabels[f] || f}</td>
                        <td className="px-3 py-2">
                          <select data-testid={`map-select-${f}`} value={mapped}
                            onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value }))}
                            className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm">
                            <option value="">{L("— non presente —", "— not present —")}</option>
                            {analysis.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                          {isSuggested && (
                            <div className="mt-1.5 flex items-center gap-2">
                              <span className="text-xs text-amber-700 dark:text-amber-400">{L("Suggerito", "Suggested")}: <b>{sug.column}</b> ({conf}%)</span>
                              <Button size="sm" variant="outline" data-testid={`accept-${f}`} className="h-6 px-2 text-xs" onClick={() => acceptSuggestion(f)}><Check className="w-3 h-3 mr-1" />{L("Accetta", "Accept")}</Button>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {mapped && tier === "auto"
                            ? <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "color-mix(in srgb, var(--demand-low) 16%, transparent)", color: "var(--demand-low)" }}>{conf}% auto</span>
                            : mapped && tier === "suggested"
                              ? <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "color-mix(in srgb, var(--demand-medium) 18%, transparent)", color: "var(--demand-medium)" }}>{L("confermato", "confirmed")}</span>
                              : mapped ? <span className="text-xs text-muted-foreground">{L("manuale", "manual")}</span>
                                : isSuggested ? <span className="text-xs text-amber-600">{conf}%</span>
                                  : <span className="text-xs text-muted-foreground">{L("non presente", "not present")}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input data-testid="import-retain" type="checkbox" checked={retain} onChange={(e) => setRetain(e.target.checked)} />
                {L("Conserva il file originale (per audit e ri-mappatura)", "Keep the original file (for audit & re-mapping)")}
              </label>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAnalysis(null)} disabled={busy}>{L("Annulla", "Cancel")}</Button>
                <Button data-testid="import-confirm-btn" onClick={confirm} disabled={busy}>
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{L("Importazione…", "Importing…")}</> : L("Importa nel database", "Import into database")}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Result */}
        {result && (
          <motion.div data-testid="import-result" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-6 mt-6">
            <div className="flex items-center gap-2 text-lg font-semibold mb-3" style={{ color: "var(--demand-low)" }}><CheckCircle2 className="w-5 h-5" />{L("Importazione completata", "Import completed")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              {[
                [L("Record totali", "Total records"), result.batch.summary.records_total],
                [L("Nuovi", "New"), result.batch.summary.new],
                [L("Modificati", "Modified"), result.batch.summary.modified],
                [L("Invariati", "Unchanged"), result.batch.summary.unchanged],
                [L("Cancellazioni", "Cancelled"), result.batch.summary.cancelled],
                [L("Duplicati nel file", "Duplicates in file"), result.batch.summary.duplicates_in_file],
                [L("Giorni", "Days"), result.batch.summary.days],
                [L("Periodo", "Period"), result.batch.summary.period ? `${result.batch.summary.period.from} → ${result.batch.summary.period.to}` : "—"],
              ].map(([k, v], i) => (
                <div key={i} className="bg-secondary/40 rounded-lg p-3"><div className="text-[11px] uppercase text-muted-foreground font-bold">{k}</div><div className="font-data font-bold">{v ?? "—"}</div></div>
              ))}
            </div>
            {result.cancellation_report && result.cancellation_report.count > 0 && (
              <div data-testid="cancellation-report" className="mt-5 rounded-xl border p-4" style={{ borderColor: "var(--demand-high)", background: "color-mix(in srgb, var(--demand-high) 8%, transparent)" }}>
                <div className="flex items-center gap-2 font-semibold mb-2" style={{ color: "var(--demand-high)" }}>
                  <XCircle className="w-5 h-5" />{result.cancellation_report.headline}
                </div>
                <div className="space-y-2">
                  {result.cancellation_report.items.map((c, i) => (
                    <div key={i} data-testid={`cancellation-item-${i}`} className="text-sm bg-card border border-border rounded-lg p-3">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-semibold">{c.room || L("Camera", "Room")}</span>
                        <span className="text-muted-foreground">{c.check_in} → {c.check_out}</span>
                        {c.adr != null && <span className="font-data font-bold">€{Math.round(c.adr)}/notte</span>}
                        {c.channel && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">{c.channel}</span>}
                        {c.had_applied_price && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: "color-mix(in srgb, var(--primary) 15%, transparent)", color: "var(--primary)" }}>
                            {L(`Prezzo consigliato applicato${c.applied_price ? ` €${c.applied_price}` : ""}`, `Recommended price applied${c.applied_price ? ` €${c.applied_price}` : ""}`)}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs flex items-center gap-1.5" style={{ color: c.why_type === "overpriced" ? "var(--demand-high)" : "var(--muted-foreground)" }}>
                        {c.why_type === "overpriced" ? <TrendingUp className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                        {L("Analisi: ", "Analysis: ")}{c.why}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* History */}
        <div className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="text-lg font-semibold">{L("Storico importazioni", "Import history")}</h2>
            {batches.length > 0 && (
              <Button variant="outline" size="sm" data-testid="reprocess-btn" onClick={reprocess} disabled={reprocessing}>
                {reprocessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {L("Ri-analizza dati esistenti", "Re-analyze existing data")}
              </Button>
            )}
          </div>
          {batches.length > 0 && (
            <p className="text-xs text-muted-foreground mb-3 -mt-1">{L("Applica i nuovi riconoscimenti (Trattamento, Data prenotazione, strategie) ai file già caricati, senza ricaricarli.", "Apply the latest recognitions (meal plan, booking date, strategies) to already-uploaded files, without re-uploading.")}</p>
          )}
          {batches.length === 0 ? (
            <p className="text-sm text-muted-foreground">{L("Nessuna importazione ancora.", "No imports yet.")}</p>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm" data-testid="import-history">
                <thead><tr className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-bold px-3 py-2">{L("File", "File")}</th>
                  <th className="text-left font-bold px-3 py-2">{L("Fonte dati", "Data source")}</th>
                  <th className="text-left font-bold px-3 py-2">{L("Data", "Date")}</th>
                  <th className="text-left font-bold px-3 py-2">{L("Periodo", "Period")}</th>
                  <th className="text-right font-bold px-3 py-2">{L("Record", "Records")}</th>
                  <th className="px-3 py-2"></th>
                </tr></thead>
                <tbody>
                  {batches.map((b, i) => (
                    <tr key={b.batch_id} data-testid={`history-row-${i}`} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{b.filename}<span className="block text-[10px] text-muted-foreground">v{b.importer_version}{b.retained ? "" : ` · ${L("file eliminato", "file deleted")}`}</span></td>
                      <td className="px-3 py-2 text-muted-foreground">{dsLabel(b.data_source || b.content_type)}{b.pms_name ? ` · ${b.pms_name}` : ""}</td>
                      <td className="px-3 py-2 font-data text-xs">{new Date(b.created_at).toLocaleString(lang === "it" ? "it-IT" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-3 py-2 font-data text-xs">{b.summary?.period ? `${b.summary.period.from} → ${b.summary.period.to}` : "—"}</td>
                      <td className="px-3 py-2 text-right font-data">{b.summary?.records_total ?? "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <button data-testid={`history-delete-${i}`} onClick={() => del(b.batch_id)} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"><Trash2 className="w-3.5 h-3.5" />{L("Elimina", "Delete")}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Learning-loop popup: did the applied advice work? */}
      <Dialog open={!!learning} onOpenChange={(o) => { if (!o) setLearning(null); }}>
        <DialogContent className="max-w-lg" data-testid="learning-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              {L("Il consiglio applicato ha funzionato?", "Did the applied advice work?")}
            </DialogTitle>
            <DialogDescription>
              {((learning?.changed_items || learning?.new_items)?.length || 0) > 0
                ? L(`In questo import: ${(learning.changed_items || learning.new_items).length} esito/i aggiornato/i sui consigli applicati. Ogni consiglio è valutato sulle prenotazioni ricevute per quella data.`,
                    `In this import: ${(learning.changed_items || learning.new_items).length} updated outcome(s) on your applied advice. Each advice is evaluated against the bookings received for that date.`)
                : L("Nessun nuovo esito in questo import. I consigli ancora in attesa di una prenotazione compariranno quando arriverà una vendita.",
                    "No new outcome in this import. Advices still waiting for a booking will appear once a sale arrives.")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-2 my-2">
            {Object.entries(learning?.summary || {}).map(([k, n]) => {
              const r = LEARN_RESULT[k] || LEARN_RESULT.pending;
              return (
                <div key={k} data-testid={`learning-chip-${k}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: `color-mix(in srgb, ${r.color} 15%, transparent)`, color: r.color }}>
                  <r.Icon className="w-3.5 h-3.5" />{L(r.it, r.en)} · {n}
                </div>
              );
            })}
          </div>

          <div className="max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border/60">
            {((learning?.changed_items || learning?.new_items) || []).slice(0, 20).map((r, i) => {
              const res = LEARN_RESULT[r.result] || LEARN_RESULT.pending;
              return (
                <div key={r.id || i} data-testid={`learning-item-${i}`} className="flex items-start gap-2 px-3 py-2">
                  <res.Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: res.color }} />
                  <div className="min-w-0 text-sm">
                    <span className="font-data font-semibold">{r.date}</span>
                    <span className="text-muted-foreground"> · €{Math.round(r.applied_price)} {L("applicato", "applied")}</span>
                    <p className="text-xs text-muted-foreground">{r.verdict}</p>
                  </div>
                </div>
              );
            })}
            {((learning?.changed_items || learning?.new_items)?.length || 0) === 0 && (
              <p className="px-3 py-4 text-sm text-muted-foreground">{L("Nessun nuovo esito. Vedi tutti gli esiti in “La mia struttura → Storico Applicati”.", "No new outcome. See all outcomes in “My property → Applied history”.")}</p>
            )}
          </div>

          <DialogFooter>
            <Button data-testid="learning-popup-close" onClick={() => setLearning(null)}>
              {L("Ho capito", "Got it")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DataHealthPanel({ health, L, lang }) {
  const score = health.quality_score ?? 0;
  const color = score >= 80 ? "var(--demand-low)" : score >= 55 ? "var(--demand-medium)" : "var(--demand-high)";
  const ring = `conic-gradient(${color} ${score * 3.6}deg, var(--secondary) 0deg)`;
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { day: "numeric", month: "short", year: "numeric" }) : "—";
  return (
    <motion.div data-testid="data-health" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-6 mb-8">
      <div className="flex items-center gap-2 mb-5">
        <HeartPulse className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">{L("Salute e completezza dei dati", "Data Health & Completeness")}</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
        {/* Score ring */}
        <div className="flex items-center gap-5">
          <div className="relative w-28 h-28 rounded-full grid place-items-center" style={{ background: ring }}>
            <div className="absolute inset-2 rounded-full bg-card grid place-items-center">
              <div className="text-center">
                <div className="text-3xl font-bold font-data" data-testid="quality-score" style={{ color }}>{score}</div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">/ 100</div>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground font-bold"><Gauge className="w-3.5 h-3.5" />{L("Precisione prezzo consigliato", "Recommended price accuracy")}</div>
            <div className="text-2xl font-bold font-data" data-testid="price-accuracy" style={{ color }}>{health.price_accuracy}%</div>
            <div className="text-xs text-muted-foreground mt-1">
              {L("Storico", "History")}: <b>{health.history_months}</b> {L("mesi", "months")} · {L("agg.", "upd.")} {fmtDate(health.last_update)}
              {health.days_since_update != null && ` (${health.days_since_update}${L("g fa", "d ago")})`}
            </div>
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {(health.metrics || []).map((m) => (
            <div key={m.key} data-testid={`metric-${m.key}`} className="flex items-start gap-2 rounded-lg border border-border px-3 py-2">
              {m.present
                ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--demand-low)" }} />
                : <div className="w-4 h-4 mt-0.5 shrink-0 rounded-full border-2 border-muted-foreground/40" />}
              <div className="min-w-0">
                <div className={`text-sm font-medium ${m.present ? "" : "text-muted-foreground"}`}>{m.label}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {m.present ? (m.source ? `${L("da", "from")} ${m.source}` : L("presente", "present")) : L("mancante", "missing")}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Suggestions */}
      {health.suggestions?.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="health-suggestions">
          {health.suggestions.map((s, i) => (
            <div key={i} className="flex items-start gap-2 text-sm rounded-md bg-secondary/40 px-3 py-2">
              <ArrowRight className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
              <span>{s}</span>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

const TRUST_ALERT = {
  red: { color: "var(--demand-high)", it: "Dati sospetti", en: "Suspicious data", Icon: ShieldAlert },
  yellow: { color: "var(--demand-medium)", it: "Da verificare", en: "To review", Icon: ShieldQuestion },
  green: { color: "var(--demand-low)", it: "Affidabile", en: "Reliable", Icon: ShieldCheck },
};
const BM_STATUS = {
  full: { it: "Contribuisce a peso pieno al benchmark", en: "Contributes at full weight to the benchmark" },
  reduced: { it: "Contribuisce a peso ridotto al benchmark", en: "Contributes at reduced weight to the benchmark" },
  review: { it: "In verifica: non contribuisce ancora al benchmark", en: "Under review: not contributing to the benchmark yet" },
  blocked: { it: "Escluso dal benchmark condiviso", en: "Excluded from the shared benchmark" },
};

function TrustScorePanel({ trust, L, lang }) {
  const score = trust.trust_score ?? 0;
  const a = TRUST_ALERT[trust.data_alert] || TRUST_ALERT.yellow;
  const color = a.color;
  const ring = `conic-gradient(${color} ${score * 3.6}deg, var(--secondary) 0deg)`;
  const subs = [
    ["data_quality", L("Qualità dati", "Data quality")],
    ["market_coherence", L("Coerenza mercato", "Market coherence")],
    ["verification", L("Verifica proprietario", "Owner verification")],
    ["import_history", L("Storico import", "Import history")],
    ["stability", L("Stabilità", "Stability")],
    ["account_age", L("Anzianità account", "Account age")],
  ];
  const anomalies = [...(trust.anomalies || [])].sort((x, y) => ({ high: 0, medium: 1, low: 2 }[x.level] - { high: 0, medium: 1, low: 2 }[y.level]));
  return (
    <motion.div data-testid="trust-panel" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-6 mb-8">
      <div className="flex items-center gap-2 mb-1">
        <a.Icon className="w-5 h-5" style={{ color }} />
        <h2 className="text-lg font-semibold">{L("Affidabilità dei dati (Trust Score)", "Data Trust Score")}</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">{L("Determina quanto i tuoi dati influenzano il benchmark condiviso del mercato. Più alto è, più conta la tua struttura.", "Determines how much your data influences the shared market benchmark. The higher it is, the more your structure counts.")}</p>

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
        <div className="flex items-center gap-5">
          <div className="relative w-28 h-28 rounded-full grid place-items-center" style={{ background: ring }}>
            <div className="absolute inset-2 rounded-full bg-card grid place-items-center">
              <div className="text-center">
                <div className="text-3xl font-bold font-data" data-testid="trust-score" style={{ color }}>{score}</div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">/ 100</div>
              </div>
            </div>
          </div>
          <div>
            <div className="inline-flex items-center gap-1.5 text-sm font-bold" style={{ color }} data-testid="trust-alert"><a.Icon className="w-4 h-4" />{lang === "en" ? a.en : a.it}</div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
              <Lock className="w-3 h-3" />{(BM_STATUS[trust.benchmark_status] || {})[lang === "en" ? "en" : "it"] || "—"}
            </div>
            {trust.owner_verified && <div className="text-xs mt-1 inline-flex items-center gap-1" style={{ color: "var(--demand-low)" }}><ShieldCheck className="w-3 h-3" />{L("Proprietario verificato", "Owner verified")}</div>}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {subs.map(([k, lbl]) => {
            const v = trust.breakdown?.[k];
            const c = v == null ? "var(--muted-foreground)" : v >= 70 ? "var(--demand-low)" : v >= 40 ? "var(--demand-medium)" : "var(--demand-high)";
            return (
              <div key={k} data-testid={`trust-sub-${k}`} className="rounded-lg border border-border px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{lbl}</div>
                <div className="font-data font-bold" style={{ color: c }}>{v != null ? Math.round(v) : "—"}</div>
              </div>
            );
          })}
        </div>
      </div>

      {anomalies.length > 0 && (
        <div className="mt-5" data-testid="trust-anomalies">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{L("Anomalie rilevate", "Detected anomalies")}</div>
          <div className="space-y-1.5">
            {anomalies.map((an, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: an.level === "high" ? "var(--demand-high)" : an.level === "medium" ? "var(--demand-medium)" : "var(--muted-foreground)" }} />
                {lang === "en" ? an.message_en : an.message_it}
              </div>
            ))}
          </div>
        </div>
      )}

      {trust.tips?.length > 0 && (
        <div className="mt-5 rounded-lg bg-secondary/40 p-4" data-testid="trust-tips">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-2 flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5 text-primary" />{L("Come aumentare il tuo Trust Score", "How to increase your Trust Score")}</div>
          <div className="space-y-1.5">
            {trust.tips.map((tp, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <ArrowRight className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                <span>{lang === "en" ? tp.en : tp.it}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

function AvailabilityPanel({ avail, L }) {
  const a = avail.availability || {};
  const cal = avail.calendar || [];
  const occs = cal.map((c) => c.occupancy).filter((v) => v != null);
  const avgOcc = occs.length ? Math.round(occs.reduce((s, v) => s + v, 0) / occs.length) : null;
  const SRC = {
    config: L("Da configurazione struttura", "From property configuration"),
    booking_profile: L("Dedotto dal profilo Booking", "Deduced from Booking profile"),
    history_rooms: L("Dedotto dalle camere nello storico", "Deduced from rooms in history"),
    history_peak: L("Stimato dal picco di occupazione", "Estimated from peak occupancy"),
  };
  const val = a.validation || {};
  const VAL = {
    coerente: { label: L("Coerente", "Consistent"), color: "var(--demand-low)", icon: ShieldCheck },
    verificare: { label: L("Verificare", "Verify"), color: "var(--demand-medium)", icon: AlertTriangle },
    scostamento: { label: L("Scostamento rilevato", "Deviation detected"), color: "var(--demand-high)", icon: AlertTriangle },
    no_report: { label: L("Nessun report da confrontare", "No report to compare"), color: "var(--muted-foreground)", icon: FileSpreadsheet },
    "n/a": { label: L("Non disponibile", "Not available"), color: "var(--muted-foreground)", icon: FileSpreadsheet },
  };
  const vinfo = VAL[val.status] || VAL["n/a"];
  const uncertain = a.rooms_source && a.rooms_source !== "config";

  return (
    <motion.div data-testid="availability-panel" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-6 mb-8">
      <div className="flex items-center gap-2 mb-5">
        <BedDouble className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">{L("Disponibilità ricostruita", "Reconstructed availability")}</h2>
      </div>

      {!a.reconstructable ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-4 py-3 text-sm dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          {L("Impossibile ricostruire l'occupazione: carica la lista prenotazioni e assicurati che il numero di camere sia impostato, oppure importa il file Disponibilità.", "Occupancy can't be reconstructed: upload the reservations list and set the number of rooms, or import the Availability file.")}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-secondary/40 rounded-lg p-3" data-testid="avail-total-rooms">
              <div className="text-[11px] uppercase text-muted-foreground font-bold">{L("Totale camere", "Total rooms")}</div>
              <div className="font-data font-bold text-lg">{a.total_rooms}</div>
              <div className="text-[10px] text-muted-foreground">{SRC[a.rooms_source] || a.rooms_source}</div>
            </div>
            <div className="bg-secondary/40 rounded-lg p-3" data-testid="avail-avg-occ">
              <div className="text-[11px] uppercase text-muted-foreground font-bold">{L("Occupazione media", "Avg occupancy")}</div>
              <div className="font-data font-bold text-lg">{avgOcc != null ? `${avgOcc}%` : "—"}</div>
            </div>
            <div className="bg-secondary/40 rounded-lg p-3" data-testid="avail-coverage-days">
              <div className="text-[11px] uppercase text-muted-foreground font-bold">{L("Giorni coperti", "Days covered")}</div>
              <div className="font-data font-bold text-lg">{a.coverage_days}</div>
            </div>
            <div className="bg-secondary/40 rounded-lg p-3" data-testid="avail-validation">
              <div className="text-[11px] uppercase text-muted-foreground font-bold">{L("Confronto col report", "Report check")}</div>
              <div className="flex items-center gap-1.5 mt-1 text-sm font-bold" style={{ color: vinfo.color }}>
                <vinfo.icon className="w-4 h-4" />{vinfo.label}
              </div>
            </div>
          </div>

          {uncertain && (
            <div className="flex items-start gap-2 rounded-md bg-secondary/40 px-3 py-2 text-sm mb-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--demand-medium)" }} />
              <span>{L(`Numero camere stimato (${a.total_rooms}). Conferma il valore nella configurazione della struttura per un'occupazione precisa.`, `Estimated room count (${a.total_rooms}). Confirm it in the property settings for accurate occupancy.`)}</span>
            </div>
          )}
          {val.status === "scostamento" || val.status === "verificare" ? (
            <div className="flex items-start gap-2 rounded-md px-3 py-2 text-sm" style={{ backgroundColor: `color-mix(in srgb, ${vinfo.color} 12%, transparent)` }}>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: vinfo.color }} />
              <span>{L(`Scostamento medio del ${val.avg_deviation_pct}% su ${val.compared_days} giorni rispetto al report giornaliero. La ricostruzione resta la fonte primaria; verifica il totale camere o prenotazioni mancanti.`, `Average ${val.avg_deviation_pct}% deviation over ${val.compared_days} days vs the daily report. Reconstruction stays the primary source; check total rooms or missing reservations.`)}</span>
            </div>
          ) : null}
        </>
      )}
    </motion.div>
  );
}
